/**
 * Ticket — a door artifact minted at fulfillment; doc id `t__{orderId}__{n}`
 * (deterministic — idempotent create). Door here is the physical venue
 * entrance at check-in. Carries NO holder PII, ever: the scan role can
 * query this collection group, so its queryable surface must be worthless
 * beyond tokens that role can already redeem. The PDF renders the holder
 * from the order.
 *
 * `origin` is the one provenance fact that surface carries: a two-value
 * discriminant naming what created the parent order, identifying nobody, so a
 * guest-only door can refuse a sold ticket of the same type without a read
 * path to the order it belongs to. The order's `issuedBy` stays on the order
 * alone — copying it down would put member identity on the collection every
 * `tickets.checkin.scan` account may list, and a door decides admission from
 * what made the order, never from who made it.
 *
 * `entryWindow` sits inside that same constraint rather than against it: a
 * catalog schedule fact — the span this sold ticket admits within, frozen from
 * its type at fulfillment — already public on the ticket type it was copied
 * from and naming nobody. It rides the ticket so the door decides admission
 * from the document it has already read, with no second lookup to pay for.
 *
 * Path: `workspaces/{orgId}/events/{eventId}/tickets/{ticketId}` — redeem is
 * a client-direct conditional update (`valid → redeemed`); unredeem is a
 * supervisor correction of ANY admission (`redeemed → valid`), gated on
 * `tickets.checkin.undo` — a tier the ordinary `tickets.checkin.scan` door
 * does not carry, so who holds that grant is the whole of what bounds an
 * undo; scanner qrToken query, member lookup read.
 *
 * The redeem rule lives in a hand fragment (`ican.extraMatches`, root
 * `package.json`): its admission clause compares `request.time` — which has
 * no kernel counterpart — against the NESTED `entryWindow.closesAt` bound,
 * the last possible check-in time, and the kernel's condition language is
 * single-segment by design (the recording proxy and the Firestore emitter
 * both reject a nested field path). This entity therefore declares no `redeem` action, so
 * nothing generated competes with the fragment — the same pattern `Checkout`
 * uses for its nested-map write rules. `unredeem` stays generated below and
 * is deliberately NOT time-bounded: a supervisor reverses a mistaken
 * admission whenever it surfaces, and a closed window must not strand a
 * wrongly-redeemed ticket.
 *
 * `SecretToken` is the shared CSPRNG-credential primitive every
 * capability/redemption token in this package is minted from.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import type { Timestamp } from "firebase/firestore";
import { z } from "zod";
import { OrderOriginSchema } from "./order-origin.const.ts";
import type { Actor } from "./workspace.entity.ts";

// ≥ 64 bits entropy — 16 chars is the hex-equivalent floor; CSPRNG-minted, never derived.
export const SecretToken = z.string().min(16);

export const TicketStatusSchema = z.enum(["valid", "redeemed", "cancelled"]);

// events/{eventId}/tickets/{ticketId} — orderId/typeId stay genuine
// reference fields; eventId is NOT stored, since Ticket nests under its
// event and the document's own path already carries it.
export const TicketSchema = z.object({
	qrToken: SecretToken.brand<"QrToken">().describe(
		"the one-time redemption credential — the QR carries only this, written once when the ticket is created",
	),
	orderId: z.string(),
	typeId: z.string(),
	// Nullish, not defaulted: a ticket minted before this field existed carries
	// no key, and every ingress site safeParses a live doc into a door verdict —
	// a required field would render each of them unrecognized at the entrance.
	// A default would be worse: it would erase "never backfilled" from the data,
	// leaving every such ticket asserting `sale` unfalsifiably.
	origin: OrderOriginSchema.nullish().describe(
		"what created the order this ticket belongs to, copied from Order.origin at mint",
	),
	status: TicketStatusSchema.default("valid"),
	// Nullish, not defaulted, for origin's reason above: a ticket minted before
	// this field existed carries no key, and a required field would render each
	// of them unrecognized at the entrance. A default would be worse — it would
	// erase "never backfilled" from the data, leaving every such ticket
	// asserting a window no operator ever set.
	entryWindow: z
		.object({ opensAt: z.custom<Timestamp>(), closesAt: z.custom<Timestamp>() })
		.nullish()
		.describe(
			"the span this ticket admits within, resolved from its type at fulfillment (resolveEntryWindow) and frozen here — so moving a programme day afterwards does not move a ticket already sold. The redeem rule compares request.time against closesAt alone, on the ticket's own document, which is why the fact rides the ticket rather than being read back off the catalog; opensAt rides alongside it for the storefront's programme-day categorization, never as a door bound. Absent fails OPEN: a ticket carrying no window admits for the event's whole span, as it was sold",
		),
	redeemedAt: z.custom<Timestamp>().nullish(),
	redeemedBy: z
		.string()
		.nullish()
		.describe(
			"the acting account that scanned it — a door phone or a named admin login, whichever surface performed the redemption",
		),
	undoneAt: z
		.custom<Timestamp>()
		.nullish()
		.describe(
			"the last time this ticket's redemption was reversed — a soft-undo audit trail, never cleared; a ticket may be undone and re-admitted more than once",
		),
	undoneBy: z
		.string()
		.nullish()
		.describe(
			"the supervisor account that reversed the admission — pinned to the acting uid by the unredeem rule below, and independent of the redeemedBy it corrects: any door's admission may be reversed by any account holding tickets.checkin.undo",
		),
});
export type Ticket = z.infer<typeof TicketSchema>;
// A writer that lets the schema fill status in types its literal against
// this input shape, not the output one (mirrors OrderInput).
export type TicketInput = z.input<typeof TicketSchema>;

export const Ticket = entity<Actor>()({
	schema: TicketSchema,
	actions: ["read", "list", "unredeem"] as const,
})
	.pipe(({ can, hasPermission, and, pathVar }) => [
		// Member lookup: attendee-list / order-support reads.
		can("read").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.reports.view", {
					event: pathVar("eventId"),
				}),
				me.mfa.eq(true),
			),
		),
		// The attendee-list QUERY itself (docs/system-design/database/orders.md's
		// authority table names this list for tickets.reports.view) — a separate
		// can('list') beside the read grant above because the emitter and the
		// kernel disagree on what a list-safe read rule implies: Firestore's own
		// `allow read` line is shorthand for get+list, so the EMITTER already
		// folds this read grant into list eligibility with no rule of its own,
		// but the kernel's me.can('list', …) consults only rules whose action
		// list carries 'list' — it does no such promotion. Without this rule the
		// kernel denied a list the generated Firestore rules already granted
		// (the ticket/reports-viewer-list-granted equivalence failure).
		can("list").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.reports.view", {
					event: pathVar("eventId"),
				}),
				me.mfa.eq(true),
			),
		),
		// Scanner: a permission-gated read, reached via the manual-lookup path's
		// direct doc get.
		can("read").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.checkin.scan", {
					event: pathVar("eventId"),
				}),
				me.mfa.eq(true),
			),
		),
		// Scanner: the same permission-gated grant backs tickets.sync.ts's own
		// live, whole-collection listener over one event's `tickets`
		// subcollection (scanner/src/tickets/tickets.sync.ts) — this
		// rule scopes the caller, not the query shape; the listener's own
		// header explains why it stays deliberately unfiltered.
		can("list").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.checkin.scan", {
					event: pathVar("eventId"),
				}),
				me.mfa.eq(true),
			),
		),
		// The redeem CAS itself (valid → redeemed, redeemedAt/redeemedBy
		// pinned, field mask closed to the trio, admission bounded by the
		// ticket's own frozen entryWindow) is the extraMatches fragment the
		// header names — no can('redeem') here, or the generated grant would
		// OR past the fragment's time clause.
		//
		// Soft undo: a supervisor reverses ANY admission on the door, not
		// only one their own account made — a phone works the door all
		// evening while the accounts carrying tickets.checkin.undo are the
		// owner and whoever is explicitly granted it, so a correction that
		// only its own scanner could make would be a correction nobody
		// present can make. No time window either: a wrongly-redeemed ticket
		// must not be stranded by a closed door. The undo grant is the whole
		// boundary — the seeded `checkin` role carries scan alone, so a door
		// phone admits and never reverses. undoneAt/undoneBy record each
		// correction, last-write-wins.
		can("unredeem")
			.when((data, me) =>
				and(
					hasPermission(me.permissions, "tickets.checkin.undo", {
						event: pathVar("eventId"),
					}),
					me.mfa.eq(true),
					data.status.eq("redeemed"),
				),
			)
			.writes((w) =>
				w
					.update({
						status: "valid",
						redeemedAt: null,
						redeemedBy: null,
						undoneAt: "serverTime",
						undoneBy: "me.uid",
					})
					.from((d) => d.status.eq("redeemed")),
			),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/events/{eventId}/tickets/{ticketId}",
		}),
	);

/**
 * The printed reference a ticket shows its holder — a short, stable digest of
 * the ticket's own doc id. Never the doc id itself: that encodes the order id,
 * so printing it leaks one buyer's order key onto a ticket they may hand to
 * someone else. Every surface that renders a ticket derives it here, so the
 * reference on the buyer's screen is the reference on the file we email them.
 */
export function ticketDisplayId(ticketId: string): string {
	const digest =
		Array.from(ticketId).reduce(
			(hash, ch) => (hash * 31 + ch.charCodeAt(0)) >>> 0,
			0,
		) % 1_000_000;
	return `T-${String(digest).padStart(6, "0")}`;
}
